import React, { useMemo } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { themeSettings } from "./theme/theme";
import { useSelector } from "react-redux";
import ScrollTop from "components/ScrollTop";
import Layout from "pages/layout";
import { checkRoles, getPermissionFlags } from "utils/permissions";
import { publicRoutes, layoutRoutes } from "./routes/routeConfig";
import RouteGuard from "./routes/RouteGuard";

function App() {
  const mode = useSelector((state) => state.mode);
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  const isAuth = Boolean(useSelector((state) => state.token));

  const user = useSelector((state) => state.user);

  const accessControl = useMemo(
    () => ({
      ...getPermissionFlags(user),
      ...checkRoles(user),
    }),
    [user]
  );

  const buildElement = ({ element, requiresAuth, canAccess }) => {
    if (!requiresAuth && !canAccess) {
      return element;
    }

    const isAllowed = canAccess ? canAccess(accessControl) : true;

    return (
      <RouteGuard
        element={element}
        isAuth={isAuth}
        requiresAuth={requiresAuth}
        isAllowed={isAllowed}
      />
    );
  };

  return (
    <div className="app">
      <HelmetProvider>
        <BrowserRouter>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <ScrollTop />
            <Routes>
              {publicRoutes.map(({ path, element }) => (
                <Route key={path} path={path} element={element} />
              ))}
              <Route path="/" element={<Layout />}>
                {layoutRoutes.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={buildElement(route)}
                  />
                ))}
              </Route>

              <Route path="*" element={<Navigate to="/home" />} />
            </Routes>
          </ThemeProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
